import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { getAllDecisionEngines } from 'lib/api';
import CustomButton from 'lib/designSystem/Button';
import PageHeader from 'lib/designSystem/PageHeader';
import { errorHandler } from 'lib/functions/errors';
import { DecisionEngine as DecisionEngineType } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

export type DecisionEngineOptions = {
  clientDecisionEngineId?: number;
  payersDecisionEngineId?: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
  canUseClientDecisionEngine?: boolean;
  canUsePayersDecisionEngine?: boolean;
  handleCancel: () => void;
  handleSubmit: (opts: DecisionEngineOptions) => void;
  showClientDecisionEngine?: boolean;
  showPayersDecisionEngine?: boolean;
};

export type DecisionEngine = DecisionEngineType & {
  revisionObjective?: string;
};

const isDefaultCreditLineEngine = (engine: DecisionEngine): boolean =>
  engine.revisionObjectiveId === 'creditLine' && engine.selected === true;

const isDefaultCreditLinePayersEngine = (engine: DecisionEngine): boolean =>
  engine.revisionObjectiveId === 'creditLine-payers' &&
  engine.selected === true;

const ConfirmAssignationDialog: React.FC<Props> = ({
  open,
  onClose,
  canUseClientDecisionEngine = true,
  canUsePayersDecisionEngine = true,
  handleCancel,
  handleSubmit,
  showClientDecisionEngine = true,
  showPayersDecisionEngine,
}) => {
  const dispatch = useDispatch();
  const [decisionEngines, setDecisionEngines] = useState<DecisionEngine[]>([]);
  const [payersDecisionEngines, setPayersDecisionEngines] = useState<
    DecisionEngine[]
  >([]);
  const [runClientDecision, setRunClientDecision] = useState<boolean>(false);
  const [clientDecisionEngineId, setClientDecisionEngineId] = useState<
    number | undefined
  >(undefined);
  const [runPayersDecision, setRunPayersDecision] = useState<boolean>(false);
  const [payersDecisionEngineId, setPayersDecisionEngineId] = useState<
    number | undefined
  >(undefined);

  const defaultCreditLineEngineId = useMemo(() => {
    const defaultEngine = decisionEngines.find(isDefaultCreditLineEngine);
    return defaultEngine?.id;
  }, [decisionEngines]);

  const disabled = useMemo(() => {
    if (runClientDecision && !clientDecisionEngineId) {
      return true;
    }
    if (runPayersDecision && !payersDecisionEngineId) {
      return true;
    }
    return false;
  }, [
    canUseClientDecisionEngine,
    canUsePayersDecisionEngine,
    runClientDecision,
    runPayersDecision,
  ]);

  const fetchAllDecisionEngines = async () => {
    try {
      const response = await getAllDecisionEngines(dispatch);
      const decisionesCreditLine = response.filter(
        (engine: DecisionEngine) => engine.revisionObjectiveId === 'creditLine',
      );
      setDecisionEngines(decisionesCreditLine);
      const decisionesCreditLinePayers = response.filter(
        (engine: DecisionEngine) =>
          engine.revisionObjectiveId === 'creditLine-payers',
      );
      setPayersDecisionEngines(decisionesCreditLinePayers);
    } catch (error) {
      errorHandler(dispatch, error, 'Error al obtener los motores de decisión');
    }
  };

  useEffect(() => {
    void fetchAllDecisionEngines();
  }, []);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        <PageHeader.TitleOnly
          iconType="edit"
          title="Nueva asignación"
          subtitle="Asigna una línea de crédito a un cliente."
        />
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Estás a punto de asignar una línea de crédito. Si lo necesitas, puedes
          ejecutar el motor de decisión para revisar la asignación de la línea.
        </Typography>
        {showClientDecisionEngine && (
          <>
            <FormControl
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runClientDecision}
                    disabled={!canUseClientDecisionEngine}
                    onChange={(e) => {
                      setRunClientDecision(e.target.checked);
                      if (
                        e.target.checked &&
                        defaultCreditLineEngineId != null
                      ) {
                        setClientDecisionEngineId(defaultCreditLineEngineId);
                      } else if (!e.target.checked) {
                        setClientDecisionEngineId(undefined);
                      }
                    }}
                  />
                }
                label="Deseo ejecutar el motor de decisión para el cliente"
              />
              {runClientDecision && (
                <FormControl>
                  <InputLabel id="client-decision-engine-label">
                    Motor de decisión (cliente)
                  </InputLabel>
                  <Select
                    labelId="client-decision-engine-label"
                    value={clientDecisionEngineId ?? ''}
                    label="Motor de decisión (cliente)"
                    onChange={(e) =>
                      setClientDecisionEngineId(
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccionar motor</em>
                    </MenuItem>
                    {decisionEngines.map((engine) => (
                      <MenuItem key={engine.id} value={engine.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                          }}
                        >
                          <span>{engine.name}</span>
                          {isDefaultCreditLineEngine(engine) && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            >
                              (Predeterminado)
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </FormControl>
          </>
        )}

        {showPayersDecisionEngine && (
          <>
            <FormControl
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={runPayersDecision}
                    disabled={!canUsePayersDecisionEngine}
                    onChange={(e) => {
                      setRunPayersDecision(e.target.checked);
                      if (
                        e.target.checked &&
                        defaultCreditLineEngineId != null
                      ) {
                        setPayersDecisionEngineId(defaultCreditLineEngineId);
                      } else if (!e.target.checked) {
                        setPayersDecisionEngineId(undefined);
                      }
                    }}
                  />
                }
                label="Deseo ejecutar el motor de decisión para los pagadores"
              />
              {runPayersDecision && (
                <FormControl>
                  <InputLabel id="payers-decision-engine-label">
                    Motor de decisión (pagadores)
                  </InputLabel>
                  <Select
                    labelId="payers-decision-engine-label"
                    value={payersDecisionEngineId ?? ''}
                    label="Motor de decisión (pagadores)"
                    onChange={(e) =>
                      setPayersDecisionEngineId(
                        e.target.value ? Number(e.target.value) : undefined,
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Seleccionar motor</em>
                    </MenuItem>
                    {payersDecisionEngines.map((engine) => (
                      <MenuItem key={engine.id} value={engine.id}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                          }}
                        >
                          <span>{engine.name}</span>
                          {isDefaultCreditLinePayersEngine(engine) && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                color: 'primary.main',
                                fontWeight: 600,
                              }}
                            >
                              (Predeterminado)
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </FormControl>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton onClick={handleCancel} color="secondary">
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() =>
            handleSubmit({
              clientDecisionEngineId,
              payersDecisionEngineId,
            })
          }
          color="primary"
          disabled={disabled}
        >
          Enviar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmAssignationDialog;
